import React from 'react';
import toast from 'react-hot-toast';
import {
  Info, Calendar, Users, Send, FileText, MessageCircle, BookOpen, Check, Clock, Search, MoreVertical, LayoutTemplate, ChevronDown
} from 'lucide-react';
import { getWhatsappTemplates } from '../../api/services/noticeApi';
import LoadingOverlay from '../../ui/LoadingOverlay';
import RecipientSelector from '../Common/RecipientSelector';

export default function NoticeForm({
  initial = {},
  onSubmit,
  classOptions = [],
  loading = false,
}) {
  const safeInitial = initial || {};
  const [form, setForm] = React.useState({
    title: safeInitial.title || '',
    content: safeInitial.content || '',
    category: safeInitial.category || 'Notice',
    priority: safeInitial.priority || 'Medium',
    applicableUnits: safeInitial.applicableUnits || [],
    applicableSections: safeInitial.applicableSections || [],
    sendWhatsapp: true,
  });

  const [isScheduled, setIsScheduled] = React.useState(false);
  const [scheduling, setScheduling] = React.useState({
    date: safeInitial.scheduledDate ? new Date(safeInitial.scheduledDate).toISOString().split('T')[0] : '',
    time: safeInitial.scheduledDate 
      ? new Date(safeInitial.scheduledDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) 
      : '10:00'
  });

  const [whatsappTemplates, setWhatsappTemplates] = React.useState([]);
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = React.useState(null);
  const [whatsappParams, setWhatsappParams] = React.useState({});
  const [targetAudience, setTargetAudience] = React.useState('both');

  const fetchWhatsappTemplates = async () => {
    try {
      const res = await getWhatsappTemplates();
      if (res.success && Array.isArray(res.data)) {
        setWhatsappTemplates(res.data);
      }
    } catch (err) {}
  };

  React.useEffect(() => {
    fetchWhatsappTemplates();
  }, []);

  const handleWhatsappTemplateSelect = (templateName) => {
    const template = whatsappTemplates.find(t => t.templateName === templateName);
    setSelectedWhatsappTemplate(template || null);
    
    const initialParams = {};
    const params = template?.parameterFields || template?.masterTemplate?.bodyParams || template?.bodyParams || [];
    
    params.forEach(p => {
      const pName = typeof p === 'string' ? p : p.name;
      const lowerName = pName.toLowerCase();
      if (lowerName !== 'student_name' && lowerName !== 'name') {
        initialParams[pName] = '';
      }
    });
    
    setWhatsappParams(initialParams);

    if (template) {
       setForm(prev => ({ 
         ...prev, 
         category: template.event || prev.category,
         title: template.event || prev.title 
       }));
    }
  };

  const getGeneratedPreview = () => {
    if (!selectedWhatsappTemplate) return form.content;
    const templateData = selectedWhatsappTemplate.masterTemplate || selectedWhatsappTemplate.templateId || selectedWhatsappTemplate;
    
    if (!templateData) return form.content;
    let preview = templateData.bodyText || '';
    const params = selectedWhatsappTemplate.parameterFields || templateData.bodyParams || [];
    
    params.forEach((p, index) => {
      const pName = typeof p === 'string' ? p : p.name;
      const lowerName = pName.toLowerCase();
      const placeholder = `{{${index + 1}}}`;
      
      let value = '';
      if (lowerName === 'student_name' || lowerName === 'name') {
        value = '[Student Name]';
      } else {
        value = whatsappParams[pName] || `[${typeof p === 'string' ? p : (p.label || p.name)}]`;
      }
      
      preview = preview.replace(placeholder, value);
    });
    return preview;
  };

  const handleClassClick = (unitId) => {
    setForm(f => {
      const exists = f.applicableUnits.includes(unitId);
      const newUnits = exists ? f.applicableUnits.filter(id => id !== unitId) : [...f.applicableUnits, unitId];
      let newSections = f.applicableSections;
      if (exists) {
        const cls = classOptions.find(c => c.value === unitId);
        if (cls?.sections) {
          const sectionIds = cls.sections.map(s => s._id);
          newSections = f.applicableSections.filter(id => !sectionIds.includes(id));
        }
      }
      return { ...f, applicableUnits: newUnits, applicableSections: newSections };
    });
  };

  const handleSectionClick = (sectionId) => {
    setForm(f => {
      const exists = f.applicableSections.includes(sectionId);
      const newSections = exists ? f.applicableSections.filter(id => id !== sectionId) : [...f.applicableSections, sectionId];
      return { ...f, applicableSections: newSections };
    });
  };

  const onFinalSubmit = (action = 'sent') => {
    if (!form.title) { toast.error("Title is required!"); return; }
    if (form.applicableUnits.length === 0) { toast.error("Please select at least one recipient!"); return; }
    
    let status = 'sent';
    let finalScheduledDate = null;
    let finalScheduledTime = null;

    if (action === 'draft') {
      status = 'draft';
    } else if (action === 'schedule' || (action === 'sent' && isScheduled)) {
      if (!scheduling.date) {
        toast.error("Please select a date to schedule!");
        return;
      }
      status = 'scheduled';
      
      try {
        const [hours, minutes] = (scheduling.time || "10:00").split(':').map(Number);
        const [year, month, day] = scheduling.date.split('-').map(Number);
        
        // Create Date object using local components to avoid UTC shifting
        const d = new Date(year, month - 1, day, hours, minutes, 0, 0);
        
        if (isNaN(d.getTime())) {
          throw new Error("Invalid date");
        }

        finalScheduledDate = d.toISOString();
        
        // Format time to "HH:mm AM/PM" for display/backend preference
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const h12 = hours % 12 || 12;
        finalScheduledTime = `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      } catch (e) {
        console.error("Scheduling Error:", e);
        toast.error("Please provide a valid date and time for scheduling.");
        return;
      }
    }

    const templateData = selectedWhatsappTemplate?.masterTemplate || selectedWhatsappTemplate?.templateId || selectedWhatsappTemplate;
    const params = selectedWhatsappTemplate?.parameterFields || templateData?.bodyParams || [];

    const payload = {
      ...form,
      title: form.title,
      content: getGeneratedPreview(),
      templateId: selectedWhatsappTemplate?._id || selectedWhatsappTemplate?.id,
      whatsappTemplate: selectedWhatsappTemplate?.templateName,
      whatsappParams: params.map(p => {
        const pName = typeof p === 'string' ? p : p.name;
        const lowerName = pName.toLowerCase();
        if (lowerName === 'student_name' || lowerName === 'name') {
          return '{{student_name}}';
        }
        return whatsappParams[pName];
      }) || [],
      targetAudience,
      status,
      scheduledDate: finalScheduledDate,
      scheduledTime: finalScheduledTime
    };
    onSubmit(payload, isScheduled ? 'scheduled' : (action === 'draft' ? 'draft' : 'sent'));
  };

  return (
    <>
      {loading && (
        <LoadingOverlay 
          message="Processing Broadcast" 
          subtext="Synchronizing with official channels..."
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column: Form Fields */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Template Selection */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Communication Type</h3>
            </div>
            <div className="p-5">
              <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">WhatsApp Template</label>
              <select 
                className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                value={selectedWhatsappTemplate?.templateName || ''}
                onChange={(e) => handleWhatsappTemplateSelect(e.target.value)}
              >
                <option value="">Select Template...</option>
                {whatsappTemplates.map(t => (
                  <option key={t.templateName} value={t.templateName}>{t.event || t.templateName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Target Audience */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Target Audience</h3>
            </div>
            <div className="p-5 flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <RecipientSelector
                  value={targetAudience}
                  onChange={setTargetAudience}
                  size="sm"
                />
              </div>
              
              <div className="md:w-64 md:border-l md:pl-8 space-y-4">
                 <div>
                    <label className="flex items-center justify-between cursor-pointer group">
                      <div>
                        <p className="text-sm font-bold text-gray-900">Schedule later</p>
                        <p className="text-[10px] text-gray-400 font-medium">Auto-dispatch at a specific time</p>
                      </div>
                      <div 
                        onClick={() => setIsScheduled(!isScheduled)}
                        className={`w-10 h-5 px-1 rounded-full flex items-center transition-all ${isScheduled ? 'bg-indigo-600' : 'bg-gray-200'}`}
                      >
                        <div className={`w-3.5 h-3.5 bg-white rounded-full transition-all ${isScheduled ? 'translate-x-[1.25rem]' : 'translate-x-0'}`} />
                      </div>
                    </label>
                 </div>

                 {isScheduled && (
                   <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Date</label>
                        <div className="relative">
                          <input 
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            value={scheduling.date}
                            onChange={(e) => setScheduling(s => ({ ...s, date: e.target.value }))}
                            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500 transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Time (15m steps)</label>
                        <input 
                          type="time"
                          step="900" 
                          value={scheduling.time}
                          onChange={(e) => setScheduling(s => ({ ...s, time: e.target.value }))}
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500 transition-all"
                        />
                      </div>
                   </div>
                 )}
              </div>
            </div>
          </div>

          {/* Recipients */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Recipients</h3>
              <span className="text-xs text-gray-500">{form.applicableUnits.length} Classes Selected</span>
            </div>
            
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[350px] overflow-y-auto">
                 {classOptions.map((c) => {
                    const isSelected = form.applicableUnits.includes(c.value);
                    return (
                      <div 
                        key={c.value} 
                        className={`p-4 rounded-md border transition-all ${
                          isSelected 
                            ? 'border-gray-900 bg-gray-50' 
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleClassClick(c.value)}
                          className="w-full flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <BookOpen size={14} className={isSelected ? 'text-gray-900' : 'text-gray-400'} />
                            <span className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                              {c.label}
                            </span>
                          </div>
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && <Check size={10} strokeWidth={3} />}
                          </div>
                        </button>

                        {isSelected && c.sections?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-bold text-gray-500 uppercase">Target Sections</label>
                              {!c.sections.some(s => form.applicableSections.includes(s._id)) && (
                                <span className="text-[10px] text-amber-600 font-bold animate-pulse flex items-center gap-1">
                                  <Info size={10} /> Select at least one section
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {c.sections.map(sec => {
                                const isSecSelected = form.applicableSections.includes(sec._id);
                                return (
                                  <button
                                    key={sec._id}
                                    type="button"
                                    onClick={() => handleSectionClick(sec._id)}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                                      isSecSelected 
                                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100' 
                                        : 'bg-gray-100 text-gray-600 border border-gray-100 hover:border-gray-300'
                                    }`}
                                  >
                                    {sec.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                 })}
              </div>
            </div>
          </div>

          {/* Message Parameters */}
          {selectedWhatsappTemplate && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Message Content</h3>
              </div>
              
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">Notice Title (Internal)</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                    className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                    placeholder="e.g. Exam Schedule Change Notice"
                  />
                </div>

                {(selectedWhatsappTemplate.parameterFields || selectedWhatsappTemplate.masterTemplate?.bodyParams || selectedWhatsappTemplate.bodyParams || [])
                  .filter(param => {
                    const paramName = (typeof param === 'string' ? param : param.name).toLowerCase();
                    return paramName !== 'student_name' && paramName !== 'name';
                  })
                  .map((param) => {
                    const paramName = typeof param === 'string' ? param : param.name;
                    const label = typeof param === 'string' 
                      ? paramName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                      : param.label;
                    const type = typeof param === 'string' 
                      ? (paramName.toLowerCase().includes('date') ? 'date' : (paramName.toLowerCase().includes('time') ? 'time' : 'text'))
                      : param.type;
                    const placeholder = typeof param === 'string' 
                      ? `Enter ${label.toLowerCase()}...`
                      : param.placeholder;

                    return (
                      <div key={paramName}>
                        <label className="text-xs font-medium text-gray-500 uppercase block mb-1.5">
                          {label} {typeof param !== 'string' && param.required && <span className="text-red-500">*</span>}
                        </label>
                        {paramName === 'content' || paramName === 'reason' || paramName === 'body' || (typeof param !== 'string' && param.type === 'textarea') ? (
                          <textarea
                            value={whatsappParams[paramName] || ''}
                            onChange={(e) => setWhatsappParams(v => ({ ...v, [paramName]: e.target.value }))}
                            className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all resize-none"
                            rows={4}
                            placeholder={placeholder}
                          />
                        ) : (
                          <input
                            type={type === 'date' ? 'date' : (type === 'number' ? 'number' : (type === 'time' ? 'time' : 'text'))}
                            value={whatsappParams[paramName] || ''}
                            onChange={(e) => setWhatsappParams(v => ({ ...v, [paramName]: e.target.value }))}
                            className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                            placeholder={placeholder}
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: WhatsApp Preview */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-8 lg:h-[calc(100vh-120px)] flex flex-col gap-4">
            
            {/* Authentic WhatsApp Container */}
            <div className="bg-[#e5ddd5] rounded-3xl shadow-xl overflow-hidden border border-gray-200 flex flex-col max-w-[400px] mx-auto flex-1 relative">
              
              {/* WhatsApp Header */}
              <div className="bg-[#075e54] px-4 py-3 flex items-center gap-3 shrink-0">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="text-white w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-bold text-[14px] leading-tight">Official Channel</h4>
                   <p className="text-[11px] text-white/80 font-medium">Online</p>
                </div>
                <div className="flex gap-4">
                  <Search size={18} className="text-white/80" />
                  <MoreVertical size={18} className="text-white/80" />
                </div>
              </div>

              {/* Chat Body */}
              <div className="flex-1 p-6 relative overflow-y-auto" style={{ 
                backgroundColor: '#e5ddd5',
                backgroundImage: `radial-gradient(#d1d1d1 1px, transparent 1px)`,
                backgroundSize: '20px 20px',
              }}>
                <div className="flex flex-col justify-end min-h-full">
                  
                  {/* Date Tag */}
                  <div className="self-center mx-auto mb-6">
                    <span className="bg-white/80 backdrop-blur-sm px-4 py-1.5 rounded-lg text-[10px] font-black text-gray-400 uppercase tracking-widest shadow-sm">Today</span>
                  </div>

                  {/* Message Bubble */}
                  <div className="relative max-w-[85%] mb-4 animate-in slide-in-from-left-4 duration-500 group">
                    <div className="bg-white p-3 px-4 rounded-xl rounded-bl-none shadow-[0_1px_2px_rgba(0,0,0,0.1)] border border-gray-100 relative z-10">
                      <p className="text-[11px] text-gray-900 leading-relaxed font-medium whitespace-pre-wrap">
                        {selectedWhatsappTemplate ? getGeneratedPreview() : (form.content || "Ready to broadcast your message...")}
                      </p>
                      <div className="flex justify-end items-center gap-1 mt-1">
                        <span className="text-[10px] text-gray-400 font-bold">{scheduling.time}</span>
                        <div className="flex -space-x-1">
                          <Check className="h-3 w-3 text-sky-400" />
                          <Check className="h-3 w-3 text-sky-400" />
                        </div>
                      </div>
                    </div>
                    {/* The "Bottom-Left" WhatsApp Tail */}
                    <div className="absolute bottom-0 -left-1.5 w-4 h-4 bg-white rounded-bl-sm rotate-45 border-l border-b border-gray-100 z-0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-3 shrink-0">
              <button
                type="button"
                onClick={() => onFinalSubmit('sent')}
                disabled={loading || (form.sendWhatsapp && !selectedWhatsappTemplate) || form.applicableUnits.length === 0}
                className="w-full py-3 bg-gray-900 text-white rounded-md flex items-center justify-center gap-2 hover:bg-black transition-all disabled:opacity-50"
              >
                {isScheduled ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                <span className="text-sm font-semibold">{isScheduled ? "Schedule Notice" : "Send Notice"}</span>
              </button>
              
              <button
                type="button"
                onClick={() => onFinalSubmit('draft')}
                disabled={loading}
                className="w-full py-3 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all text-sm font-semibold"
              >
                Save as Draft
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
